import { PromoData, TimelineEntry } from '@blisspointmedia/bpm-types/dist/OfflineInputs';
import { awaitJSON, MetricsLambdaFetch } from '../../../../utils/fetch-utils';
import { SectionKeyType, SectionStatusType } from '../data-qa-constants';
import {
  CampaignBreakoutRow,
  ConfigSummary,
  DeleteMMMDataQACommentBody,
  LoadResult,
  MMMDataQAComment,
  MMMDataQACommentThread,
  MMMDataQAResolvedThread,
  MMMDataQAThreadScope,
  PostMMMDataQACommentBody,
  PriorApproval,
  ResolveMMMDataQACommentThreadBody
} from '../data-qa-types';

export const fetchConfigs = async (cid: string): Promise<ConfigSummary[]> => {
  const res = await MetricsLambdaFetch('/getMMMDataQAConfigs', { params: { company: cid } });
  return awaitJSON<ConfigSummary[]>(res);
};

export const loadConfig = async (cid: string, configPath: string): Promise<LoadResult> => {
  const res = await MetricsLambdaFetch('/getMMMDataQALoad', {
    params: { company: cid, configPath }
  });
  return awaitJSON<LoadResult>(res);
};

// The promo periods encoded on the Offline Inputs page's Promos tab -- the same request
// that page makes (see OfflineInputs/OfflineInputsUtils.ts's fetchPromosData).
export const fetchPromoTimeline = async (cid: string): Promise<TimelineEntry[]> => {
  const res = await MetricsLambdaFetch('/getOfflineInputsPromoData', { params: { company: cid } });
  const data = await awaitJSON<PromoData>(res);
  return data.timeline ?? [];
};

export const fetchCampaignBreakout = async (
  cid: string,
  startDate: string,
  endDate: string
): Promise<CampaignBreakoutRow[]> => {
  const res = await MetricsLambdaFetch('/getMMMDataQACampaignBreakout', {
    params: { company: cid, startDate, endDate }
  });
  return awaitJSON<CampaignBreakoutRow[]>(res);
};

export const finalizeApproval = async (
  cid: string,
  configHash: string,
  sectionStatus: Record<SectionKeyType, SectionStatusType>,
  sectionNotes: Record<SectionKeyType, string>,
  fingerprints: PriorApproval['fingerprints']
): Promise<PriorApproval> => {
  const res = await MetricsLambdaFetch('/finalizeMMMDataQAApproval', {
    method: 'POST',
    body: JSON.stringify({ cid, configHash, sectionStatus, sectionNotes, fingerprints })
  });
  return awaitJSON(res);
};

// --- Comments: one thread per tab of a model --------------------------------------------

// Every open thread for the client, across its models: the app decides which model's
// thread each tab uses (see tabThreadOwner).
export const fetchCommentThreads = async (cid: string): Promise<MMMDataQACommentThread[]> => {
  const res = await MetricsLambdaFetch('/getMMMDataQAComments', { params: { company: cid } });
  return awaitJSON<MMMDataQACommentThread[]>(res);
};

// One tab's resolved threads across all of the client's models, most recently resolved
// first.
export const fetchResolvedThreads = async (
  cid: string,
  { tab, stage }: Pick<MMMDataQAThreadScope, 'tab' | 'stage'>
): Promise<MMMDataQAResolvedThread[]> => {
  const res = await MetricsLambdaFetch('/getMMMDataQAComments', {
    params: { company: cid, resolved: 'true', tab, stage }
  });
  return awaitJSON<MMMDataQAResolvedThread[]>(res);
};

export const postComment = async (body: PostMMMDataQACommentBody): Promise<MMMDataQAComment> => {
  const res = await MetricsLambdaFetch('/postMMMDataQAComment', {
    method: 'POST',
    body: JSON.stringify(body)
  });
  return awaitJSON<MMMDataQAComment>(res);
};

export const resolveCommentThread = async (
  body: ResolveMMMDataQACommentThreadBody
): Promise<void> => {
  const res = await MetricsLambdaFetch('/resolveMMMDataQACommentThread', {
    method: 'POST',
    body: JSON.stringify(body)
  });
  await awaitJSON(res);
};

export const deleteComment = async (body: DeleteMMMDataQACommentBody): Promise<void> => {
  const res = await MetricsLambdaFetch('/deleteMMMDataQAComment', {
    method: 'POST',
    body: JSON.stringify(body)
  });
  await awaitJSON(res);
};
